import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import scoresService from '../../services/scoresService'
import attitudesService from '../../services/attitudesService'
import { AttitudeCategory } from '../../services/types'
import { Tabs } from '../../components/Tabs'
import { AttitudeCard } from '../../components/AttitudeCard'
import { ConfirmModal } from '../../components/ConfirmModal'

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

export const SelfAssessmentPage = () => {
  const toast = useToast()
  const queryClient = useQueryClient()

  const now = new Date()
  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()

  // Month/Year selection state
  const [selectedMonth, setSelectedMonth] = useState(currentMonth)
  const [selectedYear, setSelectedYear] = useState(currentYear)

  // Build available month options (current year only, up to current month)
  const availableMonths = Array.from({ length: currentMonth }, (_, i) => ({
    month: i + 1,
    year: currentYear,
    label: `${MONTH_NAMES[i]}/${currentYear}`,
  }))

  // State
  const [activeTab, setActiveTab] = useState<'positive' | 'negative'>('positive')
  const [selectedAttitudes, setSelectedAttitudes] = useState<Set<number>>(new Set())
  const [observation, setObservation] = useState('')
  const [showAntifoundationModal, setShowAntifoundationModal] = useState(false)
  const [antifoundationId, setAntifoundationId] = useState<number | null>(null)
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false)

  // Fetch my assessment status for selected month
  const { data: assessmentStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['my-assessment-status', selectedMonth, selectedYear],
    queryFn: () => scoresService.getMyAssessmentStatus(selectedMonth, selectedYear),
  })

  // Fetch attitudes
  const { data: attitudes = [], isLoading: isLoadingAttitudes } = useQuery({
    queryKey: ['attitudes'],
    queryFn: () => attitudesService.getAll(),
  })

  // Pre-fill form when status is rejected (resubmission)
  const isRejected = assessmentStatus?.status === 'rejected'
  const isPending = assessmentStatus?.status === 'pending'
  const isApproved = assessmentStatus?.status === 'approved'
  const isAdminCreated = assessmentStatus?.is_admin_created === true
  const hasNoSubmission = assessmentStatus?.status === null
  // Show form only if no submission exists, or if rejected (resubmit), but NOT if admin already created
  const showForm = (hasNoSubmission || isRejected) && !isAdminCreated

  // Pre-fill from rejected score
  const rejectedScore = isRejected ? assessmentStatus?.score : null

  // Initialize selected attitudes from rejected score (only once)
  const [initialized, setInitialized] = useState(false)
  const [initializedKey, setInitializedKey] = useState('')
  const rejectedKey = `${selectedMonth}-${selectedYear}-rejected`
  if (rejectedScore && (!initialized || initializedKey !== rejectedKey)) {
    setSelectedAttitudes(new Set(rejectedScore.selected_attitudes))
    setObservation(rejectedScore.observation || '')
    setInitialized(true)
    setInitializedKey(rejectedKey)
  }

  // Reset form when switching months (if not rejected)
  const [lastMonthKey, setLastMonthKey] = useState(`${selectedMonth}-${selectedYear}`)
  const monthKey = `${selectedMonth}-${selectedYear}`
  if (monthKey !== lastMonthKey) {
    if (!isRejected) {
      setSelectedAttitudes(new Set())
      setObservation('')
      setInitialized(false)
    }
    setLastMonthKey(monthKey)
  }

  // Separate attitudes by type
  const positiveAttitudes = attitudes
    .filter((a) => a.type === 'positive')
    .sort((a, b) => a.display_order - b.display_order)

  const negativeAttitudes = attitudes
    .filter((a) => a.type === 'negative')
    .sort((a, b) => a.display_order - b.display_order)

  // Calculate points
  const calculatePoints = (): number => {
    const selected = Array.from(selectedAttitudes)
      .map((id) => attitudes.find((a) => a.id === id))
      .filter(Boolean) as AttitudeCategory[]

    const positives = selected.filter((a) => a.type === 'positive')
    const highestPositive = positives.length > 0
      ? Math.max(...positives.map((a) => a.points))
      : 0

    const negatives = selected.filter((a) => a.type === 'negative')
    const totalNegative = negatives.reduce((sum, a) => sum + a.points, 0)

    return highestPositive + totalNegative
  }

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: scoresService.selfAssess,
    onSuccess: () => {
      toast.success('Autoavaliação enviada com sucesso!')
      setSelectedAttitudes(new Set())
      setObservation('')
      setShowConfirmSubmit(false)
      queryClient.invalidateQueries({ queryKey: ['my-assessment-status', selectedMonth, selectedYear] })
      queryClient.invalidateQueries({ queryKey: ['pending-count'] })
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Erro ao enviar autoavaliação.'
      toast.error(message)
      setShowConfirmSubmit(false)
    },
  })

  // Toggle attitude
  const toggleAttitude = (id: number) => {
    const attitude = attitudes.find((a) => a.id === id)
    if (!attitude) return

    const newSelected = new Set(selectedAttitudes)

    if (newSelected.has(id)) {
      newSelected.delete(id)
      setSelectedAttitudes(newSelected)
      return
    }

    if (attitude.is_special_antifoundation) {
      setAntifoundationId(id)
      setShowAntifoundationModal(true)
      return
    }

    newSelected.add(id)
    setSelectedAttitudes(newSelected)
  }

  const confirmAntifoundation = () => {
    if (antifoundationId) {
      const newSelected = new Set(selectedAttitudes)
      newSelected.add(antifoundationId)
      setSelectedAttitudes(newSelected)
    }
    setShowAntifoundationModal(false)
    setAntifoundationId(null)
  }

  const handleSubmit = () => {
    if (selectedAttitudes.size === 0) {
      toast.warning('Selecione ao menos uma atitude.')
      return
    }
    setShowConfirmSubmit(true)
  }

  const confirmSubmit = () => {
    submitMutation.mutate({
      selected_attitudes: Array.from(selectedAttitudes),
      observation: observation.trim() || undefined,
      month: selectedMonth,
      year: selectedYear,
    })
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [m, y] = e.target.value.split('-').map(Number)
    setSelectedMonth(m)
    setSelectedYear(y)
  }

  const totalPoints = calculatePoints()
  const score = assessmentStatus?.score

  // Check eligibility
  if (isLoadingStatus) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
      </div>
    )
  }

  if (!assessmentStatus?.has_employee || !assessmentStatus?.is_eligible || !assessmentStatus?.self_assessment_enabled) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          Minha Autoavaliação
        </h1>
        <div className="bg-warning/10 border border-warning/20 rounded-card p-6">
          <p className="text-gray-700 dark:text-gray-300">
            {!assessmentStatus?.has_employee
              ? 'Seu usuário não possui perfil de funcionário vinculado. Entre em contato com o administrador.'
              : !assessmentStatus?.is_eligible
              ? 'Você não é elegível para o programa Ownership no momento.'
              : 'A autoavaliação não está habilitada no momento. Aguarde o administrador liberar.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Minha Autoavaliação
        </h1>

        {/* Month Selector */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Mês de referência:
          </label>
          <select
            value={`${selectedMonth}-${selectedYear}`}
            onChange={handleMonthChange}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {availableMonths.map((opt) => (
              <option key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Admin-created score info */}
      {isAdminCreated && score && (() => {
        const isNeg = score.points_snapshot <= 0
        const cardBg = isNeg
          ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700'
          : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700'
        const circleBg = isNeg
          ? 'bg-yellow-100 dark:bg-yellow-800'
          : 'bg-blue-100 dark:bg-blue-800'
        const titleColor = isNeg ? 'text-yellow-800 dark:text-yellow-300' : 'text-blue-800 dark:text-blue-300'
        const textColor = isNeg ? 'text-yellow-700 dark:text-yellow-400' : 'text-blue-700 dark:text-blue-400'
        const innerBg = isNeg ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-blue-100 dark:bg-blue-900/30'

        return (
          <div className={`${cardBg} rounded-card p-6 mb-6`}>
            <div className="flex items-start gap-3">
              <div className={`w-12 h-12 rounded-full ${circleBg} flex items-center justify-center text-2xl shrink-0`}>
                {isNeg ? '⚠️' : '📋'}
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold ${titleColor}`}>
                  {isNeg ? 'Avaliação Registrada — Atenção' : 'Avaliação Registrada pelo Administrador'}
                </h3>
                <p className={`text-sm ${textColor} mt-1`}>
                  O administrador registrou uma avaliação para {MONTH_NAMES[selectedMonth - 1]}/{selectedYear}.
                  {isNeg ? ' Busque melhorias!' : ''}
                </p>
                <div className={`mt-3 p-3 ${innerBg} rounded-btn`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${titleColor}`}>Pontuação:</span>
                    <span className={`text-lg font-bold ${
                      score.points_snapshot > 0 ? 'text-success' : score.points_snapshot < 0 ? 'text-danger' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {score.points_snapshot > 0 ? '+' : ''}{score.points_snapshot} pontos
                    </span>
                  </div>
                  {score.selected_attitudes_detail && score.selected_attitudes_detail.length > 0 && (
                    <div>
                      <p className={`text-xs font-medium ${textColor} mb-1`}>Atitudes:</p>
                      <div className="flex flex-wrap gap-1">
                        {score.selected_attitudes_detail.map((att) => (
                          <span
                            key={att.id}
                            className={`inline-flex px-2 py-0.5 rounded text-xs ${
                              att.type === 'positive'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}
                          >
                            {att.name} ({att.points > 0 ? '+' : ''}{att.points})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {score.observation && (
                    <div className="mt-2">
                      <p className={`text-xs font-medium ${textColor}`}>Observação:</p>
                      <p className={`text-sm ${textColor}`}>{score.observation}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Status Cards */}
      {isPending && !isAdminCreated && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-card p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-800 flex items-center justify-center text-2xl">
              ⏳
            </div>
            <div>
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-300">
                Aguardando Aprovação
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                Sua autoavaliação de {MONTH_NAMES[selectedMonth - 1]}/{selectedYear} está pendente de revisão pelo administrador.
                {score && ` Pontuação enviada: ${score.points_snapshot > 0 ? '+' : ''}${score.points_snapshot} pontos.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {isRejected && !isAdminCreated && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-card p-6 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center text-2xl shrink-0">
              ✗
            </div>
            <div>
              <h3 className="font-semibold text-red-800 dark:text-red-300">
                Avaliação Rejeitada
              </h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                Sua autoavaliação de {MONTH_NAMES[selectedMonth - 1]}/{selectedYear} foi rejeitada pelo administrador. Revise e reenvie.
              </p>
              {score?.reviewer_observation && (
                <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 rounded-btn">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">Motivo:</p>
                  <p className="text-sm text-red-700 dark:text-red-400">{score.reviewer_observation}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isApproved && !isAdminCreated && (() => {
        const needsAttention = score && (score.points_snapshot <= 0 || (score.original_attitudes && score.original_attitudes.length > 0))
        const cardBg = needsAttention
          ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700'
          : 'bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700'
        const circleBg = needsAttention ? 'bg-yellow-100 dark:bg-yellow-800' : 'bg-green-100 dark:bg-green-800'
        const titleColor = needsAttention ? 'text-yellow-800 dark:text-yellow-300' : 'text-green-800 dark:text-green-300'
        const textColor = needsAttention ? 'text-yellow-700 dark:text-yellow-400' : 'text-green-700 dark:text-green-400'
        const innerBg = needsAttention ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-green-100 dark:bg-green-900/30'
        const borderColor = needsAttention ? 'border-yellow-200 dark:border-yellow-800' : 'border-green-200 dark:border-green-800'

        return (
          <div className={`${cardBg} rounded-card p-6 mb-6`}>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full ${circleBg} flex items-center justify-center text-2xl`}>
                {needsAttention ? '⚠️' : '✓'}
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold ${titleColor}`}>
                  {needsAttention ? 'Avaliação Aprovada — Atenção' : 'Avaliação Aprovada'}
                </h3>
                <p className={`text-sm ${textColor}`}>
                  {needsAttention && score!.points_snapshot <= 0
                    ? `Sua avaliação de ${MONTH_NAMES[selectedMonth - 1]}/${selectedYear} resultou em ${score!.points_snapshot} pontos. Busque melhorias!`
                    : `Sua autoavaliação de ${MONTH_NAMES[selectedMonth - 1]}/${selectedYear} foi aprovada com ressalvas. ${score ? `${score.points_snapshot > 0 ? '+' : ''}${score.points_snapshot} pontos.` : ''}`
                  }
                  {!needsAttention && score && ` ${score.points_snapshot > 0 ? '+' : ''}${score.points_snapshot} pontos.`}
                </p>
              </div>
            </div>

            {/* Score details — always shown */}
            {score && (
              <div className={`mt-4 border-t ${borderColor} pt-4`}>
                {/* Diff: show before/after if admin changed attitudes */}
                {score.original_attitudes && score.original_attitudes.length > 0 ? (
                  <>
                    <h4 className={`text-sm font-semibold ${titleColor} mb-3`}>
                      Alterações feitas pelo administrador:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Before */}
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-btn border border-red-200 dark:border-red-800">
                        <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">Sua avaliação original ({score.original_points != null ? `${score.original_points > 0 ? '+' : ''}${score.original_points} pts` : ''})</p>
                        <div className="flex flex-wrap gap-1">
                          {score.original_attitudes.map((att) => (
                            <span
                              key={att.id}
                              className={`inline-flex px-2 py-0.5 rounded text-xs ${
                                att.type === 'positive'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}
                            >
                              {att.name} ({att.points > 0 ? '+' : ''}{att.points})
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* After */}
                      <div className={`p-3 ${needsAttention ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'} rounded-btn border`}>
                        <p className={`text-xs font-medium ${needsAttention ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'} mb-2`}>Aprovada com ({score.points_snapshot > 0 ? '+' : ''}{score.points_snapshot} pts)</p>
                        <div className="flex flex-wrap gap-1">
                          {score.selected_attitudes_detail?.map((att) => (
                            <span
                              key={att.id}
                              className={`inline-flex px-2 py-0.5 rounded text-xs ${
                                att.type === 'positive'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}
                            >
                              {att.name} ({att.points > 0 ? '+' : ''}{att.points})
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Approved directly — show attitudes */
                  score.selected_attitudes_detail && score.selected_attitudes_detail.length > 0 && (
                    <div className={`p-3 ${innerBg} rounded-btn`}>
                      <p className={`text-xs font-medium ${textColor} mb-2`}>Atitudes:</p>
                      <div className="flex flex-wrap gap-1">
                        {score.selected_attitudes_detail.map((att) => (
                          <span
                            key={att.id}
                            className={`inline-flex px-2 py-0.5 rounded text-xs ${
                              att.type === 'positive'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}
                          >
                            {att.name} ({att.points > 0 ? '+' : ''}{att.points})
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                )}
                {score.observation && (
                  <div className={`mt-3 p-3 ${innerBg} rounded-btn`}>
                    <p className={`text-xs font-medium ${textColor}`}>Observação:</p>
                    <p className={`text-sm ${textColor}`}>{score.observation}</p>
                  </div>
                )}
                {score.reviewer_observation && (
                  <div className={`mt-3 p-3 ${innerBg} rounded-btn`}>
                    <p className={`text-xs font-medium ${textColor}`}>Observação do revisor:</p>
                    <p className={`text-sm ${textColor}`}>{score.reviewer_observation}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })()}

      {/* Form — only if no submission yet or rejected */}
      {showForm && (
        <>
          {/* Points Summary */}
          <div className="bg-primary/10 border border-primary/20 rounded-card p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Pontuação Calculada
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {totalPoints > 0 ? '+' : ''}{totalPoints} pontos
                </p>
              </div>
              <div className="text-4xl">
                {totalPoints > 0 ? '✨' : totalPoints < 0 ? '⚠️' : '➖'}
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
              Maior atitude positiva + soma de negativas
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <Tabs
              tabs={[
                { id: 'positive', label: 'Positivo', icon: '✓' },
                { id: 'negative', label: 'Negativo', icon: '✗' },
              ]}
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab as 'positive' | 'negative')}
            />
          </div>

          {/* Attitudes Grid */}
          {isLoadingAttitudes ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Carregando atitudes...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {activeTab === 'positive'
                ? positiveAttitudes.map((attitude) => (
                    <AttitudeCard
                      key={attitude.id}
                      attitude={attitude}
                      isSelected={selectedAttitudes.has(attitude.id)}
                      onToggle={toggleAttitude}
                    />
                  ))
                : negativeAttitudes.map((attitude) => (
                    <AttitudeCard
                      key={attitude.id}
                      attitude={attitude}
                      isSelected={selectedAttitudes.has(attitude.id)}
                      onToggle={toggleAttitude}
                    />
                  ))}
            </div>
          )}

          {/* Observation */}
          <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observações (opcional)
            </label>
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder="Adicione contexto ou justificativa para sua autoavaliação..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isPending || selectedAttitudes.size === 0}
              className="px-8 py-3 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitMutation.isPending
                ? 'Enviando...'
                : isRejected
                ? 'Reenviar Autoavaliação'
                : 'Enviar Autoavaliação'}
            </button>
          </div>
        </>
      )}

      {/* Confirm Submit Modal */}
      <ConfirmModal
        isOpen={showConfirmSubmit}
        onClose={() => setShowConfirmSubmit(false)}
        onConfirm={confirmSubmit}
        title="Confirmar Envio"
        description={
          <div className="space-y-2">
            <p>
              Deseja enviar sua autoavaliação de <strong>{MONTH_NAMES[selectedMonth - 1]}/{selectedYear}</strong>?
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Pontuação: <strong>{totalPoints > 0 ? '+' : ''}{totalPoints} pontos</strong>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              A avaliação será enviada para aprovação do administrador.
            </p>
          </div>
        }
        confirmText="Sim, Enviar"
        cancelText="Cancelar"
        isLoading={submitMutation.isPending}
      />

      {/* Antifoundation Modal */}
      <ConfirmModal
        isOpen={showAntifoundationModal}
        onClose={() => {
          setShowAntifoundationModal(false)
          setAntifoundationId(null)
        }}
        onConfirm={confirmAntifoundation}
        title="Confirmar Antifundador"
        description={
          <div className="space-y-3">
            <p className="font-semibold text-danger">
              Esta é uma ação irreversível e crítica!
            </p>
            <p>
              Ao selecionar <strong>Antifundador</strong>, as seguintes consequências serão aplicadas:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Todos os pontos do trimestre corrente serão zerados</li>
              <li>Você será marcado como inelegível para Ownership</li>
            </ul>
          </div>
        }
        confirmText="Sim, Confirmar"
        cancelText="Cancelar"
        isDanger={true}
      />
    </div>
  )
}

export default SelfAssessmentPage
